import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildApiUrl, getApiBaseUrl } from '../../config/apiConfig';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { useCart } from '../../context/CartContext';
import './CoursesPage.css';
import { log, logError } from '../../utils/logger';
import { formatARS } from '../../utils/currency';

const CoursesPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { notify } = useNotifications();
  const { addToCart } = useCart();
  const [courses, setCourses] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [acquiringCourseId, setAcquiringCourseId] = useState(null);
  const [filters, setFilters] = useState({
    gratis: false,
    nuevo: false,
    oferta: false,
    categoria: ''
  });

  useEffect(() => {
    fetchCourses();
    fetchBundles();
    fetchCategories();
  }, []);

  const fetchCourses = async () => {
    try {
      // Obtener productos públicos de cursos activos
      // Incluir token si el usuario está autenticado para verificar si tiene los cursos
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(buildApiUrl('/api/products/public/courses'), {
        headers
      });
      if (!response.ok) throw new Error('Error al cargar cursos');
      const data = await response.json();
      setCourses(Array.isArray(data.products) ? data.products : []);
    } catch (error) {
      logError('[CoursesPage] Error fetching courses:', error);
    } finally {
      setLoading(false);
    }
  };

  // Función helper para obtener imagen según el tipo de producto
  const getProductImage = async (product) => {
    const referenceId = product.referenceId || product.reference_id;
    const productType = product.type;
    
    if (!referenceId || !productType) {
      return null;
    }

    try {
      if (productType === 'course') {
        const response = await fetch(buildApiUrl(`/api/courses/public/${referenceId}`));
        if (!response.ok) return null;
        const data = await response.json();
        return data.course?.imagen || null;
      } else if (productType === 'event') {
        const response = await fetch(buildApiUrl(`/api/events/public/${referenceId}`));
        if (!response.ok) return null;
        const data = await response.json();
        const eventData = data.event || {};
        return eventData.imagen || eventData.imagenevento || null;
      } else if (productType === 'item' || productType === 'items' || productType === 'physical') {
        const response = await fetch(buildApiUrl(`/api/products/public/item/${referenceId}`));
        if (!response.ok) return null;
        const data = await response.json();
        const itemData = data.product?.item || {};
        
        // Intentar obtener imagen de diferentes fuentes
        // 1. De images array (objetos con image_url)
        if (itemData.images && Array.isArray(itemData.images) && itemData.images.length > 0) {
          const firstImage = itemData.images[0];
          if (firstImage.image_url) {
            return firstImage.image_url;
          }
        }
        
        // 2. De imagenes array (strings)
        if (itemData.imagenes && Array.isArray(itemData.imagenes) && itemData.imagenes.length > 0) {
          return itemData.imagenes[0];
        }
        
        // 3. Si imagenes es un string, intentar parsearlo
        if (itemData.imagenes && typeof itemData.imagenes === 'string') {
          try {
            const parsed = JSON.parse(itemData.imagenes);
            if (Array.isArray(parsed) && parsed.length > 0) {
              return parsed[0];
            }
          } catch (e) {
            // Si no es JSON válido, usar el string directamente
            return itemData.imagenes;
          }
        }
        
        return null;
      }
      
      return null;
    } catch (err) {
      logError(`[CoursesPage] Error fetching image for ${productType} (referenceId: ${referenceId}):`, err);
      return null;
    }
  };

  const fetchBundles = async () => {
    try {
      const response = await fetch(buildApiUrl('/api/bundles/public'));
      if (!response.ok) throw new Error('Error al cargar bundles');
      const data = await response.json();
      
      // Filtrar bundles que incluyan al menos 1 producto (cualquier tipo)
      const validBundles = (data.bundles || []).filter(bundle => {
        const products = bundle.products || [];
        return products.length >= 1;
      });
      
      // Obtener información e imágenes para los primeros 2 productos de cada bundle
      const bundlesWithImages = await Promise.all(
        validBundles.map(async (bundle) => {
          const products = bundle.products || [];
          const productsToFetch = products.slice(0, 2);
          
          const productsWithImages = await Promise.all(
            productsToFetch.map(async (product) => {
              // Obtener imagen del producto
              let imageUrl = await getProductImage(product);
              
              // Si no se obtuvo imagen, intentar con campos alternativos del producto directamente
              if (!imageUrl) {
                imageUrl = product.image || product.imagen || product.imagenevento || null;
              }
              
              return {
                ...product,
                imageUrl: imageUrl,
                image: imageUrl || product.image,
                imagen: imageUrl || product.imagen,
                imagenevento: imageUrl || product.imagenevento,
                referenceId: product.referenceId || product.reference_id
              };
            })
          );
          
          // Si solo hay 1 producto, agregar un placeholder para el segundo
          if (productsWithImages.length === 1) {
            productsWithImages.push({
              id: `placeholder-${bundle.id}`,
              type: 'placeholder',
              imageUrl: null,
              isPlaceholder: true
            });
          }
          
          return {
            ...bundle,
            products: productsWithImages
          };
        })
      );
      
      setBundles(bundlesWithImages);
    } catch (error) {
      logError('[CoursesPage] Error fetching bundles:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      // Usar endpoint público o crear uno nuevo
      const response = await fetch(buildApiUrl('/api/courses/categories'));
      if (response.ok) {
        const data = await response.json();
        setCategories(Array.isArray(data.categories) ? data.categories : []);
      }
    } catch (error) {
      logError('[CoursesPage] Error fetching categories:', error);
    }
  };

  const filteredCourses = useMemo(() => {
    let filtered = courses;

    // Filtro de búsqueda
    if (search.trim()) {
      const term = search.trim().toLowerCase();
      filtered = filtered.filter(course => {
        const name = (course.course?.nombre || course.name || '').toLowerCase();
        const desc = (course.course?.descripcionBreve || course.description || '').toLowerCase();
        return name.includes(term) || desc.includes(term);
      });
    }

    // Filtro Gratis
    if (filters.gratis) {
      filtered = filtered.filter(course => course.price === 0);
    }

    // Filtro Nuevo (creado hace menos de 1 mes)
    if (filters.nuevo) {
      const oneMonthAgo = new Date();
      oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
      filtered = filtered.filter(course => {
        const createdDate = new Date(course.created_at || course.course?.created_at);
        return createdDate >= oneMonthAgo;
      });
    }

    // Filtro Oferta
    if (filters.oferta) {
      filtered = filtered.filter(course => {
        const discountPercent = parseFloat(course.discountPercent || course.course?.discountPercent || 0);
        return discountPercent > 0;
      });
    }

    // Filtro Categoría
    if (filters.categoria) {
      filtered = filtered.filter(course => {
        const courseCategoryId = course.course?.categoriaId || course.course?.categoria?.id;
        return courseCategoryId === parseInt(filters.categoria);
      });
    }

    return filtered;
  }, [courses, search, filters]);

  const calculateFinalPrice = (price, discountPercent) => {
    if (discountPercent > 0) {
      return price * (1 - discountPercent / 100);
    }
    return price;
  };

  const resolveImageUrl = (source) => {
    if (!source) return null;
    const sanitized = String(source).replace(/\\/g, '/').replace(/\s/g, '%20');
    if (/^https?:\/\//i.test(sanitized)) return sanitized;
    const base = getApiBaseUrl();
    if (sanitized.startsWith('/')) return `${base}${sanitized}`;
    if (sanitized.startsWith('uploads/')) return `${base}/${sanitized}`;
    return `${base}/uploads/${sanitized}`;
  };

  const handleAcquireFreeCourse = useCallback(async (course) => {
    if (!user || !token) {
      navigate('/login', { state: { from: '/courses' } });
      return;
    }

    if (!course.id) {
      notify({
        message: 'No se pudo obtener la información del producto.',
        type: 'error',
      });
      return;
    }

    setAcquiringCourseId(course.id);
    try {
      const response = await fetch(buildApiUrl('/api/products/grant-access'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          productId: course.id,
          productType: 'course',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
        throw new Error(errorData.error || `Error al adquirir el curso (${response.status})`);
      }

      notify({
        message: '¡Curso adquirido! El curso gratuito se agregó a tu cuenta exitosamente.',
        type: 'success',
      });
    } catch (err) {
      logError('[CoursesPage] Error adquiriendo curso gratuito:', err);
      notify({
        message: err.message || 'No se pudo adquirir el curso. Por favor, intenta nuevamente.',
        type: 'error',
      });
    } finally {
      setAcquiringCourseId(null);
    }
  }, [user, token, navigate, notify]);

  return (
    <div className="courses-page">
      {/* Hero Section */}
      <section className="courses-hero">
        <div className="courses-hero-content">
          <div className="courses-hero-text">
            <div className="courses-hero-badges">
              <span className="courses-hero-badge courses-hero-badge-primary">
                <i className="fas fa-graduation-cap"></i>
                Academia Online
              </span>
              <span className="courses-hero-badge courses-hero-badge-discount">
                <i className="fas fa-bolt"></i>
                Hasta 50% OFF
              </span>
            </div>
            <h1 className="courses-hero-title">
              Transformá tu <span className="courses-hero-title-accent">Carrera Profesional</span>
            </h1>
            <p className="courses-hero-description">
              Aprendé de los mejores profesionales con cursos <strong>100% online</strong> diseñados para 
              potenciar tus habilidades odontologicas. Accedé cuando quieras, 
              desde donde quieras.
            </p>
            
            {/* Value Propositions */}
            <div className="courses-hero-features">
              <div className="courses-hero-feature">
                <i className="fas fa-check-circle"></i>
                <span>Acceso de por vida</span>
              </div>
              <div className="courses-hero-feature">
                <i className="fas fa-check-circle"></i>
                <span>Certificado incluido</span>
              </div>
              <div className="courses-hero-feature">
                <i className="fas fa-check-circle"></i>
                <span>Soporte experto</span>
              </div>
            </div>

            <button 
              className="courses-hero-button"
              onClick={() => {
                const filtersSection = document.querySelector('.courses-filters-section');
                if (filtersSection) {
                  filtersSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
            >
              <i className="fas fa-search"></i>
              Explorar Cursos
            </button>
          </div>
        </div>
      </section>

      {/* Search and Filters Section */}
      <section className="courses-filters-section">
        <div className="courses-filters-container">
          <div className="courses-search">
            <i className="bi-search courses-search-icon" />
            <input
              type="text"
              className="courses-search-input"
              placeholder="Buscar cursos..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="courses-filters">
            <button
              className={`courses-filter-btn ${filters.gratis ? 'active' : ''}`}
              onClick={() => setFilters({ ...filters, gratis: !filters.gratis })}
            >
              <i className="bi-gift" />
              Gratis
            </button>
            <button
              className={`courses-filter-btn ${filters.nuevo ? 'active' : ''}`}
              onClick={() => setFilters({ ...filters, nuevo: !filters.nuevo })}
            >
              <i className="bi-star" />
              Nuevo
            </button>
            <button
              className={`courses-filter-btn ${filters.oferta ? 'active' : ''}`}
              onClick={() => setFilters({ ...filters, oferta: !filters.oferta })}
            >
              <i className="bi-tag" />
              Oferta
            </button>
            <select
              className="courses-filter-select"
              value={filters.categoria}
              onChange={(e) => setFilters({ ...filters, categoria: e.target.value })}
            >
              <option value="">Todas las categorías</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.nombre}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* Courses Grid */}
      <section className="courses-grid-section">
        <div className="courses-grid-container">
          <h2 className="courses-section-title">Cursos Disponibles</h2>
          {loading ? (
            <div className="courses-loading">
              <div className="courses-spinner" />
              <p>Cargando cursos...</p>
            </div>
          ) : filteredCourses.length === 0 ? (
            <div className="courses-empty">
              <i className="bi-inbox courses-empty-icon" />
              <p>No se encontraron cursos</p>
            </div>
          ) : (
            <div className="courses-grid">
              {filteredCourses.map((course) => {
                // Obtener descuento del curso o del producto
                const discountPercent = parseFloat(course.discountPercent || course.course?.discountPercent || 0);
                const finalPrice = calculateFinalPrice(course.price, discountPercent);
                const isFree = finalPrice === 0 || course.price === 0;
                const courseName = course.course?.nombre || course.course?.titulo || course.name || 'Curso sin nombre';
                const courseDesc = course.course?.descripcionBreve || course.description || '';
                // Intentar obtener imagen de múltiples fuentes
                const courseImage = resolveImageUrl(
                  course.image || 
                  course.imagen || 
                  course.course?.imagen || 
                  course.course?.image
                );
                
                return (
                  <div key={course.id} className="course-card">
                    <div className="course-card-image">
                      {courseImage ? (
                        <img src={courseImage} alt={courseName} />
                      ) : (
                        <div className="course-card-image-placeholder">
                          <i className="bi-journal-text" />
                        </div>
                      )}
                      {discountPercent > 0 && (
                        <div className="course-card-badge">
                          -{discountPercent}% OFF
                        </div>
                      )}
                    </div>
                    <div className="course-card-content">
                      <h3 className="course-card-title">{courseName}</h3>
                      <p className="course-card-description">{courseDesc}</p>
                      <div className="course-card-price">
                        {discountPercent > 0 ? (
                          <>
                            <span className="course-card-price-original">{formatARS(course.price)}</span>
                            <span className="course-card-price-final">{formatARS(finalPrice)}</span>
                          </>
                        ) : (
                          <span className="course-card-price-final">
                            {course.price === 0 ? 'Gratis' : formatARS(course.price)}
                          </span>
                        )}
                      </div>
                      <div className="course-card-actions">
                        <button
                          className="course-card-btn course-card-btn-info"
                          onClick={() => navigate(`/courses/${course.referenceId || course.id}`)}
                        >
                          <i className="bi-info-circle" />
                          Más información
                        </button>
                        {course.isAcquired ? (
                          <button
                            className="course-card-btn course-card-btn-acquired"
                            disabled
                          >
                            <i className="bi-check-circle-fill" />
                            Ya adquirido
                          </button>
                        ) : isFree ? (
                          <button
                            className="course-card-btn course-card-btn-free"
                            onClick={() => handleAcquireFreeCourse(course)}
                            disabled={acquiringCourseId === course.id || !user}
                          >
                            {acquiringCourseId === course.id ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Adquiriendo...
                              </>
                            ) : (
                              <>
                                <i className="bi-gift" />
                                Adquirir gratis!
                              </>
                            )}
                          </button>
                        ) : (
                        <button
                          className="course-card-btn course-card-btn-cart"
                          onClick={async () => {
                              const courseImage = course.image || course.imagen || course.course?.imagen || course.course?.image;
                              const imageUrl = courseImage ? resolveImageUrl(courseImage) : null;
                              
                              const success = await addToCart(
                                {
                                  id: course.referenceId || course.id,
                                  name: courseName,
                                  price: finalPrice,
                                  image: imageUrl || '',
                                  productType: 'course',
                                  quantity: 1,
                                },
                                token,
                                (errorMessage) => {
                                  notify({
                                    message: errorMessage,
                                    type: 'warning',
                                  });
                                }
                              );
                              
                              if (success) {
                                notify({
                                  message: 'El curso se agregó al carrito exitosamente.',
                                  type: 'success',
                                });
                              }
                            }}
                          >
                            <i className="bi-cart-plus" />
                            Agregar al carrito
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Bundles Section */}
      {bundles.length > 0 && (
        <section className="courses-bundles-section">
          <div className="courses-bundles-container">
            <h2 className="courses-section-title">Paquetes Especiales</h2>
            <div className="courses-bundles-grid">
              {bundles.map((bundle) => {
                const displayProducts = bundle.products || [];
                // Calcular precio final con descuento
                const bundlePrice = parseFloat(bundle.price || 0);
                const discountPercent = parseFloat(bundle.discountPercent || 0);
                const finalPrice = calculateFinalPrice(bundlePrice, discountPercent);
                
                return (
                  <div key={bundle.id} className="bundle-card">
                    <div className="bundle-card-image">
                      {displayProducts.slice(0, 2).map((product, index) => {
                        // Obtener la imagen del producto (ya procesada en fetchBundles)
                        let imageUrl = null;
                        if (product.imageUrl) {
                          imageUrl = resolveImageUrl(product.imageUrl);
                        } else if (product.image) {
                          imageUrl = resolveImageUrl(product.image);
                        } else if (product.imagen) {
                          imageUrl = resolveImageUrl(product.imagen);
                        } else if (product.imagenevento) {
                          imageUrl = resolveImageUrl(product.imagenevento);
                        }
                        
                        const isPlaceholder = product.isPlaceholder || !imageUrl;
                        
                        return (
                          <div
                            key={product.id || `placeholder-${bundle.id}-${index}`}
                            className={`bundle-card-image-part bundle-card-image-part-${index + 1}`}
                            style={{
                              backgroundImage: imageUrl ? `url(${imageUrl})` : 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#94a3b8',
                              fontSize: '2rem'
                            }}
                          >
                            {!imageUrl && <i className="bi-journal-text" />}
                          </div>
                        );
                      })}
                      {discountPercent > 0 && (
                        <div className="bundle-card-badge">
                          -{discountPercent}% OFF
                        </div>
                      )}
                    </div>
                    <div className="bundle-card-content">
                      <h3 className="bundle-card-title">{bundle.nombre}</h3>
                      <p className="bundle-card-description">{bundle.descripcion || ''}</p>
                      <div className="bundle-card-price">
                        {discountPercent > 0 ? (
                          <>
                            <span className="bundle-card-price-original">{formatARS(bundlePrice)}</span>
                            <span className="bundle-card-price-final">{formatARS(finalPrice)}</span>
                          </>
                        ) : (
                          <span className="bundle-card-price-final">{formatARS(bundlePrice)}</span>
                        )}
                      </div>
                      <button
                        className="bundle-card-btn"
                        onClick={() => {
                          // TODO: Implementar agregar bundle al carrito
                          log('Agregar bundle al carrito:', bundle.id);
                        }}
                      >
                        <i className="bi-cart-plus" />
                        Agregar al carrito
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default CoursesPage;

